import { useState } from 'react';
import { NAVY, GOLD, G50, G100, G300, G500, G700 } from '@/lib/colors';
import { crossAppHref } from '@/lib/nav';
import type { WebsiteDict } from './i18n';

type SectionProps = { t: WebsiteDict };

export function Hero({ t }: SectionProps) {
  return (
    <section className="glow-bg" style={{ padding: '80px 24px 90px', textAlign: 'center' }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 14px',
            borderRadius: 9999,
            background: 'rgba(240,165,0,0.12)',
            border: `1px solid rgba(240,165,0,0.4)`,
            color: '#92400E',
            fontSize: 12,
            fontWeight: 600,
            marginBottom: 24,
          }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD }}></span>
          {t.hero_badge}
        </div>
        <h1
          style={{
            fontFamily: 'Prompt',
            fontSize: 'clamp(36px, 6vw, 60px)',
            fontWeight: 700,
            color: NAVY,
            lineHeight: 1.1,
            marginBottom: 18,
          }}>
          <span
            style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, ${GOLD} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
            {t.hero_title_a}
          </span>
          <br />
          <span>{t.hero_title_b}</span>
        </h1>
        <p style={{ fontSize: 18, color: G500, lineHeight: 1.7, maxWidth: 680, margin: '0 auto 32px' }}>
          {t.hero_desc}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <a
            href={crossAppHref('/signup')}
            style={{
              background: GOLD,
              color: '#fff',
              padding: '14px 28px',
              borderRadius: 8,
              fontFamily: 'Sarabun',
              fontSize: 15,
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(240,165,0,0.3)',
            }}>
            {t.hero_cta}
          </a>
          <a
            href={crossAppHref('/product')}
            style={{
              background: '#fff',
              color: NAVY,
              border: `1.5px solid ${G300}`,
              padding: '13px 28px',
              borderRadius: 8,
              fontFamily: 'Sarabun',
              fontSize: 15,
              fontWeight: 600,
              textDecoration: 'none',
            }}>
            {t.hero_demo}
          </a>
        </div>
        <div style={{ fontSize: 12, color: G500 }}>{t.hero_note}</div>
      </div>
    </section>
  );
}

export function TrustStrip({ t }: SectionProps) {
  return (
    <section style={{ background: NAVY, padding: '40px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div
          style={{
            textAlign: 'center',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 13,
            marginBottom: 24,
            letterSpacing: '0.05em',
          }}>
          {t.trust_title}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24 }}>
          {t.trust.map(([n, l], i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'JetBrains Mono',
                  fontSize: 30,
                  fontWeight: 600,
                  color: GOLD,
                  marginBottom: 4,
                }}>
                {n}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function KeyBenefits({ t }: SectionProps) {
  return (
    <section style={{ padding: '80px 24px', background: '#fff' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div
            style={{
              fontFamily: 'Sarabun',
              fontSize: 13,
              fontWeight: 600,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>
            {t.bench_kicker}
          </div>
          <h2 style={{ fontFamily: 'Prompt', fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 700, color: NAVY }}>
            {t.bench_title}
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {t.benefits.map((b, i) => (
            <article key={i} style={{ background: G50, borderRadius: 14, padding: '26px 24px', border: `1px solid ${G100}` }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background:
                    i === 0 ? '#D1FAE5' :
                    i === 1 ? '#DBEAFE' :
                    i === 2 ? '#FEE2E2' :
                    i === 3 ? 'rgba(240,165,0,0.18)' : '#E0E7FF',
                  color:
                    i === 0 ? '#065F46' :
                    i === 1 ? '#1E40AF' :
                    i === 2 ? '#991B1B' :
                    i === 3 ? '#92400E' : '#3730A3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 700,
                  marginBottom: 14,
                }}>
                {b.ico}
              </div>
              <h3 style={{ fontFamily: 'Prompt', fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 8 }}>
                {b.name}
              </h3>
              <p style={{ fontSize: 14, color: G500, lineHeight: 1.6, marginBottom: 14 }}>{b.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {b.bullets.map((bb, j) => (
                  <li key={j} style={{ fontSize: 13, color: G700, marginBottom: 6, paddingLeft: 18, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: GOLD, fontWeight: 700 }}>✓</span>
                    {bb}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks({ t }: SectionProps) {
  return (
    <section style={{ padding: '80px 24px', background: G50 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div
            style={{
              fontFamily: 'Sarabun',
              fontSize: 13,
              fontWeight: 600,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>
            {t.how_kicker}
          </div>
          <h2 style={{ fontFamily: 'Prompt', fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 700, color: NAVY }}>
            {t.how_title}
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          {t.how_steps.map(([title, desc], i) => (
            <div
              key={i}
              style={{
                background: '#fff',
                borderRadius: 14,
                padding: 28,
                border: `1px solid ${G100}`,
                boxShadow: '0 4px 16px rgba(27,43,94,0.06)',
                position: 'relative',
              }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: NAVY,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Prompt',
                  fontWeight: 700,
                  fontSize: 18,
                  marginBottom: 14,
                }}>
                {i + 1}
              </div>
              <h3 style={{ fontFamily: 'Prompt', fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 8 }}>
                {title}
              </h3>
              <p style={{ fontSize: 14, color: G500, lineHeight: 1.6 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeatureHighlights({ t }: SectionProps) {
  return (
    <section id="features" style={{ padding: '80px 24px', background: '#fff' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div
            style={{
              fontFamily: 'Sarabun',
              fontSize: 13,
              fontWeight: 600,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>
            {t.feat_kicker}
          </div>
          <h2 style={{ fontFamily: 'Prompt', fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 700, color: NAVY }}>
            {t.feat_title}
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 18 }}>
          {t.feat.map(([ico, name, desc], i) => (
            <article
              key={i}
              style={{
                display: 'flex',
                gap: 14,
                padding: 20,
                borderRadius: 12,
                border: `1px solid ${G100}`,
                background: G50,
              }}>
              <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{ico}</div>
              <div>
                <h3 style={{ fontFamily: 'Prompt', fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
                  {name}
                </h3>
                <p style={{ fontSize: 13, color: G500, lineHeight: 1.6 }}>{desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQ({ t }: SectionProps) {
  const [open, setOpen] = useState(0);
  return (
    <section style={{ padding: '80px 24px', background: '#fff' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div
            style={{
              fontFamily: 'Sarabun',
              fontSize: 13,
              fontWeight: 600,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>
            {t.faq_kicker}
          </div>
          <h2 style={{ fontFamily: 'Prompt', fontSize: 'clamp(26px, 4vw, 34px)', fontWeight: 700, color: NAVY }}>
            {t.faq_title}
          </h2>
        </div>
        <div>
          {t.faqs.map(([q, a], i) => {
            const isOpen = open === i;
            return (
              <details
                key={i}
                open={isOpen}
                onToggle={e => {
                  const target = e.target as HTMLDetailsElement;
                  if (target.open) setOpen(i);
                  else if (isOpen) setOpen(-1);
                }}
                style={{
                  borderTop: i === 0 ? `1px solid ${G100}` : 'none',
                  borderBottom: `1px solid ${G100}`,
                  padding: '18px 0',
                }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: 'Prompt',
                    fontSize: 16,
                    fontWeight: 600,
                    color: NAVY,
                  }}>
                  <span>{q}</span>
                  <span
                    style={{
                      color: GOLD,
                      fontSize: 22,
                      fontWeight: 300,
                      transform: isOpen ? 'rotate(45deg)' : 'none',
                      transition: 'transform 200ms',
                    }}>
                    +
                  </span>
                </summary>
                <p style={{ marginTop: 12, fontSize: 14, color: G500, lineHeight: 1.7 }}>{a}</p>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}

